import {NgClass} from '@angular/common';
import {Component, HostListener, Inject} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {TranslationService, validators} from '@emma-labs/shared/angular';
import {
  ENVIRONMENT,
  EnvironmentBroker,
  PRO_SIGN_UP_PAGE_EN,
  PRO_SIGN_UP_PAGE_FR,
  UserRole,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Button, ButtonDirective} from 'primeng/button';
import {DividerModule} from 'primeng/divider';
import {InputTextModule} from 'primeng/inputtext';
import {Ripple} from 'primeng/ripple';
import {debounceTime, first, Subject} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {AuthService} from '../../../services/auth.service';
import {BTN_LOADING_ANIMATION_TIME, SHORT_DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {AuthBackgroundComponent} from '../auth-background/auth-background.component';

interface LoginForm {
  email: FormControl<string>;
  password: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'em-auth-page',
  templateUrl: './auth-page.component.html',
  imports: [
    AuthBackgroundComponent,
    Button,
    NgClass,
    ReactiveFormsModule,
    ButtonDirective,
    RouterLink,
    DividerModule,
    InputTextModule,
    Ripple,
  ],
})
export class AuthPageComponent {
  form: FormGroup<LoginForm>;
  credentialsError = false;
  authError = false;
  fadeOut = false;
  isButtonLoading = false;
  login$ = new Subject<void>();
  readonly dev: boolean;
  protected readonly UserRole = UserRole;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly fb: NonNullableFormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly translate: TranslationService,
    @Inject(ENVIRONMENT) private readonly environment: EnvironmentBroker,
  ) {
    this.dev = this.environment.showDevControls;
    this.form = this.createForm();

    this.login$
      .pipe(
        debounceTime(SHORT_DEBOUNCE_TIME),
        map(() => this.handleLogin()),
        untilDestroyed(this),
      )
      .subscribe();

    this.handlePasswordResetToken();
  }

  @HostListener('document:keydown.enter', ['$event']) onKeyDownEnter() {
    this.login$.next();
  }

  goToSignUpPage(): void {
    window.location.href = this.translate.currentLanguageIsFr() ? PRO_SIGN_UP_PAGE_FR : PRO_SIGN_UP_PAGE_EN;
  }

  private handleLogin(): void {
    this.isButtonLoading = true;
    this.authError = false;

    const email = this.form.controls.email.getRawValue();
    const password = this.form.controls.password.getRawValue();

    this.authService
      .login(email, password)
      .pipe(first())
      .subscribe({
        next: (result) => {
          if (result.success) {
            this.fadeOut = true;
            this.authService.handleSuccessfulLoginRedirection(result.session);
          } else {
            if (result.requiresMultifactorAuthentication) {
              this.router.navigate(['/', 'auth', 'mfa'], {
                state: {
                  email,
                  password,
                  phoneLastTwoDigits: result.phoneLastTwoDigits,
                },
              });
              return;
            }

            setTimeout(() => {
              this.isButtonLoading = false;
              this.authError = true;
            }, BTN_LOADING_ANIMATION_TIME);
          }
        },
        error: () => {
          setTimeout(() => {
            this.isButtonLoading = false;
            this.credentialsError = true;
          }, BTN_LOADING_ANIMATION_TIME);
        },
      });
  }

  simulateAgent(email: string): void {
    this.form.patchValue({password: 'emma'});
    this.form.patchValue({email});
  }

  private createForm(): FormGroup<LoginForm> {
    const form = this.fb.group({
      email: ['', Validators.required, validators.email],
      password: ['', [Validators.required]],
    });

    form.valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe(() => {
      this.credentialsError = false;
      this.authError = false;
    });

    return form;
  }

  private handlePasswordResetToken(): void {
    const invalidPasswordToken = this.activatedRoute.snapshot.queryParamMap.get('invalidPasswordToken');

    if (invalidPasswordToken) {
      this.authError = true;

      this.router.navigate([], {
        queryParams: {
          invalidPasswordToken: null,
        },
        queryParamsHandling: 'merge',
      });
    }
  }
}
